/**
 * "Export product" dialog (map toolbar). Captures the whole map view - map,
 * HUD cards, insets, callouts, legend, annotations - as one image and bundles
 * it with every widget's data (Story timeline, AI summary, route sequence,
 * cameras passed, legend) into a self-contained HTML page or a PDF. PNG saves
 * just the capture.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Code as HtmlIcon,
  PictureAsPdf as PdfIcon,
  Image as PngIcon,
} from '@mui/icons-material';
import type { RouteSequenceGroup } from '@signals/app-shell/services/routeSequence';
import { captureProductView, productCaptureRoot, type ProductCaptureResult } from '@signals/app-shell/utils/productCapture';
import {
  getStorySequence,
  getVisibleStoryEvents,
  inView,
  useNarrativeStore,
} from '../../stores/narrativeStore';
import { hudPortalContainer } from './PathfinderHudKit';
import {
  buildProductDoc,
  docToHtml,
  downloadDataUrl,
  downloadDocPdf,
  downloadText,
  type ProductLegendCategory,
} from './pathfinderHudExport';

const CLASSIFICATION_PRESETS = [
  'UNCLASSIFIED',
  'UNCLASSIFIED // FOUO',
  'CUI',
  'LAW ENFORCEMENT SENSITIVE',
  'FOR OFFICIAL USE ONLY',
  'COMMERCIAL IN CONFIDENCE',
];

const ACCENT = '#FF2332';

const safeName = (s: string) => s.replace(/[^a-z0-9-_]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'product';
const stamp = () => new Date().toISOString().slice(0, 16).replace('T', '-').replace(':', '');

export interface ProductExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Element to capture when not in fullscreen (the fullscreen target wins when active). */
  captureFallback: () => HTMLElement | null;
  routeSequenceGroups: RouteSequenceGroup[];
  legend: { cameraCategories: ProductLegendCategory[]; cameraCount: number } | null;
  /** Whether the route-sequence widget is currently shown (default checkbox state). */
  routeSequenceOpen: boolean;
}

type Format = 'html' | 'pdf' | 'png';

export const ProductExportDialog: React.FC<ProductExportDialogProps> = ({
  open,
  onClose,
  captureFallback,
  routeSequenceGroups,
  legend,
  routeSequenceOpen,
}) => {
  const storyEntities = useNarrativeStore((s) => s.storyEntities);
  const storyAiSummary = useNarrativeStore((s) => s.storyAiSummary);
  const storyAiFocus = useNarrativeStore((s) => s.storyAiFocus);
  const storyCameras = useNarrativeStore((s) => s.storyCameras);
  const storyCropToView = useNarrativeStore((s) => s.storyCropToView);
  const storyViewBounds = useNarrativeStore((s) => s.storyViewBounds);
  const storyEventsVersion = useNarrativeStore((s) => s.storyEvents);

  const [title, setTitle] = useState('');
  const [classification, setClassification] = useState('');
  const [notes, setNotes] = useState('');
  const [cropToView, setCropToView] = useState(false);
  const [include, setInclude] = useState({
    map: true,
    story: true,
    ai: true,
    routeSequence: routeSequenceOpen,
    cameras: true,
    legend: true,
  });
  const [busy, setBusy] = useState<Format | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-sync defaults each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setCropToView(false);
    setInclude({
      map: true,
      story: true,
      ai: true,
      routeSequence: true,
      cameras: true,
      legend: true,
    });
    setError(null);
    setBusy(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sequenceAll = useMemo(() => getStorySequence(), [storyEventsVersion, storyCameras]); // eslint-disable-line react-hooks/exhaustive-deps
  const sequenceInView = useMemo(
    () => (cropToView ? getVisibleStoryEvents() : sequenceAll),
    [cropToView, sequenceAll, storyViewBounds], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const camerasPassed = useMemo(() => {
    const passed = storyCameras?.passed ?? [];
    if (!cropToView || !storyViewBounds) return passed;
    return passed.filter((c) => inView(storyViewBounds, [c.lat, c.lng]));
  }, [storyCameras, cropToView, storyViewBounds]);
  const routeSequenceCount = routeSequenceGroups.reduce((n, g) => n + g.entries.length, 0);

  const toggle = (key: keyof typeof include) => (_: unknown, checked: boolean) => setInclude((i) => ({ ...i, [key]: checked }));

  const capture = async (): Promise<ProductCaptureResult | null> => {
    const root = productCaptureRoot(captureFallback());
    if (!root) return null;
    // Let the dialog's backdrop settle; captureProductView also hides
    // [data-export-hide] (this dialog) in the live tree for the snapshot.
    await new Promise((r) => setTimeout(r, 80));
    return captureProductView(root, { scale: 2 });
  };

  const run = async (format: Format) => {
    setBusy(format);
    setError(null);
    try {
      const shot = include.map || format === 'png' ? await capture() : null;
      if ((include.map || format === 'png') && !shot) {
        throw new Error('Could not capture the map view. Try again once the map has finished loading.');
      }
      const base = safeName(title || 'pathfinder-product');
      if (format === 'png') {
        downloadDataUrl(`${base}-map-${stamp()}.png`, shot!.dataUrl);
        return;
      }
      const doc = buildProductDoc({
        title,
        classification,
        notes,
        entities: storyEntities,
        events: sequenceInView,
        eventsTotal: sequenceAll.length,
        aiSummary: storyAiSummary,
        aiFocus: storyAiFocus,
        routeSequence: routeSequenceGroups.map((g) => ({
          entityId: g.entityId,
          entries: g.entries.map((e) => ({
            seq: e.seq,
            kind: e.kind,
            entityId: e.entityId,
            time: e.time,
            endTime: e.endTime,
            label: e.label,
            detail: e.detail,
            lon: e.lon,
            lat: e.lat,
            cameraId: e.cameraId,
            categoryLabel: e.categoryLabel,
            distanceM: e.distanceM,
            hits: e.hits,
          })),
        })),
        camerasPassed,
        legend,
        viewNote: cropToView ? 'data limited to the current map view' : 'full loaded dataset',
        include,
      });
      const mapCaption = `Map view at export${cropToView ? ' (data limited to this view)' : ''}`;
      if (format === 'html') {
        downloadText(
          `${base}-${stamp()}.html`,
          docToHtml(doc, { mapImageDataUrl: shot?.dataUrl ?? null, mapCaption, accent: ACCENT }),
          'text/html',
        );
      } else {
        await downloadDocPdf(doc, {
          mapImageDataUrl: shot?.dataUrl ?? null,
          mapImageAspect: shot?.aspectRatio,
          includeMap: false,
          mapFullPage: true,
          accent: ACCENT,
        });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Export failed.');
    } finally {
      setBusy(null);
    }
  };

  const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      container={hudPortalContainer}
      maxWidth="sm"
      fullWidth
      data-export-hide
      PaperProps={{ sx: { bgcolor: 'rgba(22,24,30,0.98)', color: '#e6e8ec', border: '1px solid rgba(255,255,255,0.12)' } }}
      sx={{ zIndex: 3000 }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Export product
        <Typography variant="body2" sx={{ color: 'rgba(230,232,236,0.7)', mt: 0.5 }}>
          One capture of the whole map view - map, timeline, insets, callouts, legend - plus the data behind every widget.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        <Stack spacing={2}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Subject movement 14-15 Oct"
            size="small"
            fullWidth
            autoFocus
          />
          <Autocomplete
            freeSolo
            options={CLASSIFICATION_PRESETS}
            inputValue={classification}
            onInputChange={(_, v) => setClassification(v)}
            renderInput={(params) => (
              <TextField {...params} label="Classification / handling" placeholder="Optional banner on every page" size="small" />
            )}
          />
          <TextField
            label="Analyst notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Context, key judgements, caveats… appears first in the product."
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
          />

          <Box>
            <Typography variant="overline" sx={{ color: 'rgba(230,232,236,0.6)', letterSpacing: '0.12em' }}>
              Include
            </Typography>
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', '& .MuiFormControlLabel-root': { mr: 0 } }}>
              <FormControlLabel control={<Checkbox size="small" checked={include.map} onChange={toggle('map')} />} label="Map capture" />
              <FormControlLabel
                control={<Checkbox size="small" checked={include.story && sequenceInView.length > 0} onChange={toggle('story')} disabled={sequenceInView.length === 0} />}
                label={`Story timeline (${count(sequenceInView.length, 'event')})`}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={include.ai && !!storyAiSummary} onChange={toggle('ai')} disabled={!storyAiSummary} />}
                label={storyAiSummary ? 'AI summary' : 'AI summary (none yet)'}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={include.routeSequence && routeSequenceCount > 0}
                    onChange={toggle('routeSequence')}
                    disabled={routeSequenceCount === 0}
                  />
                }
                label={`Route sequence (${count(routeSequenceCount, 'stop')})`}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={include.cameras && camerasPassed.length > 0} onChange={toggle('cameras')} disabled={camerasPassed.length === 0} />}
                label={`Cameras passed (${camerasPassed.length})`}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={include.legend && !!legend} onChange={toggle('legend')} disabled={!legend} />}
                label="Legend"
              />
            </FormGroup>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={<Checkbox size="small" checked={cropToView} onChange={(_, v) => setCropToView(v)} />}
              label={
                <Typography variant="body2">
                  Limit data to the current map view
                  {cropToView && sequenceAll.length !== sequenceInView.length ? (
                    <Typography component="span" variant="caption" sx={{ color: 'rgba(230,232,236,0.6)', ml: 1 }}>
                      {sequenceInView.length} of {sequenceAll.length} events
                    </Typography>
                  ) : null}
                </Typography>
              }
            />
          </Box>

          {error && <Alert severity="error" variant="outlined">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={!!busy} sx={{ mr: 'auto', color: 'rgba(230,232,236,0.7)' }}>
          Cancel
        </Button>
        <Button
          onClick={() => run('png')}
          disabled={!!busy}
          startIcon={busy === 'png' ? <CircularProgress size={14} color="inherit" /> : <PngIcon />}
          variant="outlined"
          color="inherit"
        >
          PNG (capture only)
        </Button>
        <Button
          onClick={() => run('html')}
          disabled={!!busy}
          startIcon={busy === 'html' ? <CircularProgress size={14} color="inherit" /> : <HtmlIcon />}
          variant="outlined"
          color="inherit"
        >
          HTML
        </Button>
        <Button
          onClick={() => run('pdf')}
          disabled={!!busy}
          startIcon={busy === 'pdf' ? <CircularProgress size={14} color="inherit" /> : <PdfIcon />}
          variant="contained"
          sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d91d2a' } }}
        >
          PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductExportDialog;
